// Источник карточек поверх BFF: тот же интерфейс, что у поддельного, но данные СВОИ.
//
// ЗАЧЕМ ЭТОТ ФАЙЛ. `source.ts` объявил семь методов и обещал, что за ними завтра окажется
// своя схема. Это «завтра»: здесь те же семь методов ходят в `/api/cards/*` (BFF → ядро →
// таблицы `schema_sys.sql`). Ни одно описание сущности, ни один экран и ни одна проверка при
// этом не менялись — в этом и был смысл интерфейса.
//
// ЧТО СЕГОДНЯ ЧЕСТНО. Своя схема накатывается частями, и данных в ней пока нет вовсе.
// Поэтому здесь ТРИ разных ответа вместо привычных двух:
//   • запись есть          → карточка;
//   • записи нет           → null (это «не найдено», и экран говорит именно так);
//   • таблицы/слоя нет     → бросаем `СлояНет` с текстом, чего именно не существует.
// Третий случай нельзя выражать пустотой. Пустой список читается человеком как ответ
// «ничего не заведено», а на деле сделку сегодня негде хранить — таблицы `deal` в схеме ещё
// нет. Разница между «пусто» и «спросить негде» — это разница между «работаем дальше» и
// «ждём переноса», и решать за человека мы не вправе.
//
// ЗАПИСЬ ЗДЕСЬ НЕ РАБОТАЕТ НАРОЧНО. `сохранить` бросает `ЗаписьНеВключена` — с текстом, а не
// молча. Раздел выходит только на чтение: включение записи — отдельный шаг и отдельное
// решение владельца по каждому разделу. Молчаливая заглушка («сделали вид, что сохранили»)
// здесь была бы худшим из возможных вариантов: человек заполнит карточку, увидит зелёное
// «сохранено» и уйдёт, а данных не будет нигде.
//
// ПОЧЕМУ СВОЙ fetch, А НЕ `api/client.ts`. Клиент BFF типизирован под ответы зеркала
// (Deal, Company, ListResult) и живёт своей жизнью. Карточки ходят в другие ручки с другой
// формой ответа, и дописывать их в чужой файл значило бы связать два расписания правок.
// Классы ошибок при этом взяты оттуда же — чтобы `401` в карточке значил ровно то же, что
// `401` в любом другом разделе, и вёл на ту же страницу входа.

import { ApiError, Unauthorized } from '../api/client'

import { описание } from './specs'
import type { ИсточникКарточек, ИтогСохранения, СтрокаСвязи } from './source'
import type {
  Деньги,
  ЗаписьЖурнала,
  ЗаписьКарточки,
  Значение,
  Значения,
  ОперацияЖурнала,
  Поле,
  Событие,
  Состояние,
  Сущность,
  Файл,
  Ярлык,
} from './types'

// ─────────────────────────── свои отказы ───────────────────────────

/**
 * Слоя ещё не существует: таблицы нет в схеме или его нет вовсе (лента событий).
 *
 * Отдельный класс, а не обычная ошибка: экран обязан отличать «сеть подвела» от «этого
 * в системе пока нет». Первое лечится повтором, второе — переносом, и человеку это разные
 * новости.
 */
export class СлояНет extends Error {
  readonly почему: string

  constructor(почему: string) {
    super(почему)
    this.name = 'СлояНет'
    this.почему = почему
  }
}

/** Запись в своей схеме ещё не включена. Бросается ВМЕСТО сохранения, а не после него. */
export class ЗаписьНеВключена extends Error {
  constructor(сущность: Сущность) {
    super(
      `${описание(сущность).один} сохранена не будет: запись из CRM в свою схему ещё не `
      + 'включена. Раздел выходит только на чтение — на полях висят бизнес-процессы, которые '
      + 'ставят задачи живым людям, и включают запись по одному разделу за раз. '
      + 'Набранное осталось в черновике этого браузера.',
    )
    this.name = 'ЗаписьНеВключена'
  }
}

// ─────────────────────────── форма ответов ядра ───────────────────────────

interface СыраяСсылка { entity?: string; id: string; title: string; hint?: string }
interface СырыеДеньги { amount: number | null; currency?: string }

interface СыраяЗапись {
  id: string
  rev: number
  state: string
  values: Record<string, unknown>
}

interface СтрокаЖурнала {
  rev: number
  op: string
  when: string
  who: string
  via?: string | null
  reason?: string | null
  field?: string | null
  from?: string | null
  to?: string | null
}

interface СтрокаФайла {
  id: string
  name: string
  size: number
  role?: string | null
  added_by?: string | null
  added_at?: string | null
}

/** Что раздел может показать сегодня: ответ `/cards/refs`. Экран берёт отсюда причину пустоты. */
export interface СведенияОКарточках {
  сущности: {
    код: string
    таблица: string
    есть: boolean
    /** Строк в таблице. null — таблицы нет, и это НЕ ноль. */
    строк: number | null
    почему: string | null
  }[]
  записьВключена: boolean
  почемуНетЗаписи: string
  лентаЕсть: boolean
  почемуНетЛенты: string
}

// ─────────────────────────── поход в сеть ───────────────────────────

const КОРЕНЬ = '/api/cards'

async function взять<T>(ручка: string, параметры: Record<string, string>): Promise<T> {
  const адрес = new URLSearchParams(параметры).toString()
  const ответ = await fetch(`${КОРЕНЬ}/${ручка}${адрес ? `?${адрес}` : ''}`, {
    credentials: 'same-origin',                  // личность знает только BFF, кука его
    headers: { 'Content-Type': 'application/json' },
  })
  if (ответ.status === 401) throw new Unauthorized()
  const текст = await ответ.text()
  const тело: unknown = текст ? JSON.parse(текст) : {}
  const запись_ = (тело ?? {}) as Record<string, unknown>
  // 501 — не поломка и не «пусто»: ядро говорит, чего в схеме ещё нет.
  if (ответ.status === 501 || запись_.error === 'no_layer') {
    throw new СлояНет(String(запись_.why ?? 'этого слоя в своей схеме ещё нет'))
  }
  // «Записи нет» — законный ответ, а не отказ, и бросать его ошибкой нельзя: экран обязан
  // отличать «такой карточки не существует» от «источник не ответил». Отдаём тело как есть,
  // а разбирается с ним вызывающий. Неизвестный ПУТЬ отвечает другим текстом ('not found'
  // через пробел) и сюда не попадает — иначе опечатка в адресе выглядела бы пустой карточкой.
  if (ответ.status === 404 && запись_.error === 'not_found') return тело as T
  if (!ответ.ok) {
    const текстОшибки = (запись_.why as string) || (запись_.error as string)
      || `HTTP ${ответ.status}`
    throw new ApiError(текстОшибки, ответ.status)
  }
  return тело as T
}

// ─────────────────────────── разбор значений ───────────────────────────

const ссылка = (v: unknown): v is СыраяСсылка =>
  typeof v === 'object' && v !== null && 'id' in v && 'title' in v

const деньги = (v: unknown): v is СырыеДеньги =>
  typeof v === 'object' && v !== null && 'amount' in v

/** Ссылка из ядра → ярлык карточки. Сущность берём из ОПИСАНИЯ поля: оно тут главное. */
function вЯрлык(поле: Поле, сырое: СыраяСсылка): Ярлык {
  return {
    сущность: (поле.связьС ?? (сырое.entity as Сущность)),
    id: String(сырое.id),
    название: String(сырое.title ?? ''),
    подсказка: сырое.hint ? String(сырое.hint) : undefined,
  }
}

/** Деньги из ядра. Валюту не подставляем: пустая валюта — это «не знаем», а не рубли. */
function вДеньги(сырое: СырыеДеньги): Деньги {
  return {
    сумма: typeof сырое.amount === 'number' ? сырое.amount : null,
    валюта: String(сырое.currency ?? ''),
  }
}

function значениеПоля(поле: Поле, сырое: unknown): Значение {
  if (сырое === null || сырое === undefined) return null
  switch (поле.вид) {
    case 'связь':
      return ссылка(сырое) ? вЯрлык(поле, сырое) : null
    case 'деньги':
      return деньги(сырое) ? вДеньги(сырое) : null
    case 'число':
      return typeof сырое === 'number' ? сырое : Number(сырое)
    case 'флаг':
      return Boolean(сырое)
    case 'файл':
      // Вложения приезжают отдельной ручкой (`документы`), а не полем записи: у файла есть
      // роль, автор и дата, и в значении карточки им места нет.
      return undefined
    default:
      return typeof сырое === 'string' ? сырое : String(сырое)
  }
}

/**
 * Сырые значения ядра → значения карточки.
 *
 * Идём по ОПИСАНИЮ, а не по тому, что прислало ядро: описание — единственное место, где
 * известно, что `amount` это деньги, а `company_id` — связь на компанию. Лишние ключи из
 * ядра сюда не попадают, а недостающие остаются пустыми — поле, которого в схеме ещё нет,
 * выглядит незаполненным, и это правда.
 */
export function значенияИзЯдра(сущность: Сущность, сырые: Record<string, unknown>): Значения {
  const оп = описание(сущность)
  const итог: Значения = {}
  for (const поле of оп.поля) {
    // Ключа нет вовсе — колонки нет в схеме или её не просили. Это НЕ пустое значение:
    // поставить сюда null значило бы сказать «поле есть и оно не заполнено», а мы не знаем
    // даже этого. Отсутствие ключа карточка покажет незаполненным полем, и это честно.
    if (!(поле.код in сырые)) continue
    const v = значениеПоля(поле, сырые[поле.код])
    if (v !== undefined) итог[поле.код] = v
  }
  // Состояние документа живёт и в значениях: на нём держится условная обязательность
  // номера. Ядро кладёт его тем же ключом — переносим как есть.
  if (typeof сырые.doc_state === 'string') итог.doc_state = сырые.doc_state
  return итог
}

const ОПЕРАЦИИ: ОперацияЖурнала[] = ['create', 'update', 'delete', 'restore']
const ОТКУДА: ЗаписьЖурнала['откуда'][] = ['ui', 'api', 'import', 'job']

function вЗаписьЖурнала(сущность: Сущность, с: СтрокаЖурнала): ЗаписьЖурнала {
  const поле = с.field
    ? описание(сущность).поля.find((п) => п.код === с.field)
    : undefined
  return {
    ревизия: с.rev,
    когда: с.when,
    кто: с.who,
    операция: ОПЕРАЦИИ.includes(с.op as ОперацияЖурнала)
      ? (с.op as ОперацияЖурнала)
      : 'update',
    поле: с.field ?? undefined,
    // Подпись поля берётся из описания, а не из журнала: в журнале лежит имя колонки, и
    // человеку оно ничего не говорит. Нет такого поля в описании — показываем код, а не
    // прочерк: код хотя бы можно найти поиском.
    подписьПоля: поле?.подпись ?? (с.field ?? undefined),
    из: с.from ?? undefined,
    во: с.to ?? undefined,
    причина: с.reason ?? undefined,
    откуда: ОТКУДА.includes(с.via as ЗаписьЖурнала['откуда'])
      ? (с.via as ЗаписьЖурнала['откуда'])
      : undefined,
  }
}

function вФайл(с: СтрокаФайла): Файл {
  return {
    id: String(с.id),
    имя: с.name,
    // Ноль — это ноль, а не «неизвестно»: неизвестного размера у файла не бывает.
    размер: typeof с.size === 'number' ? с.size : 0,
    роль: с.role ?? undefined,
    добавил: с.added_by ?? undefined,
    когда: с.added_at ?? undefined,
  }
}

// ─────────────────────────── сам источник ───────────────────────────

/** Что раздел может показать сегодня. Отдельной функцией: это про раздел, а не про карточку. */
export async function сведенияОКарточках(): Promise<СведенияОКарточках> {
  const тело = await взять<{
    entities: { code: string; table: string; exists: boolean; rows: number | null;
                why: string | null }[]
    write_enabled: boolean
    write_why: string
    feed: { exists: boolean; why: string }
  }>('refs', {})
  return {
    сущности: (тело.entities ?? []).map((e) => ({
      код: e.code, таблица: e.table, есть: e.exists,
      строк: e.exists ? (e.rows ?? null) : null,
      почему: e.why ?? null,
    })),
    записьВключена: Boolean(тело.write_enabled),
    почемуНетЗаписи: тело.write_why ?? '',
    лентаЕсть: Boolean(тело.feed?.exists),
    почемуНетЛенты: тело.feed?.why ?? '',
  }
}

export function источникBFF(): ИсточникКарточек {
  return {
    async запись(сущность, id): Promise<ЗаписьКарточки | null> {
      const тело = await взять<{ row?: СыраяЗапись; error?: string }>(
        'record', { entity: сущность, id },
      )
      // 404 приходит сюда телом `{error:'not_found'}` — взять() его не бросает, потому что
      // «не найдено» это не отказ. Отсутствие записи — законный ответ, и его отличает от
      // отказа только null.
      if (!тело.row) return null
      const с = тело.row
      return {
        id: String(с.id),
        ревизия: Number(с.rev) || 1,
        состояние: с.state as Состояние,
        значения: значенияИзЯдра(сущность, с.values ?? {}),
      }
    },

    async поиск(сущность, запрос): Promise<Ярлык[]> {
      const q = запрос.trim()
      // Одна буква — это ещё не запрос: в ядро с ней не ходим вовсе, как и поддельный
      // источник. Порог ядро объявляет в /cards/refs, но платить за него походом в сеть
      // незачем.
      if (q.length < 2) return []
      const тело = await взять<{ rows: СыраяСсылка[] }>('search', { entity: сущность, q })
      return (тело.rows ?? []).map((с) => ({
        сущность,
        id: String(с.id),
        название: String(с.title ?? ''),
        подсказка: с.hint ? String(с.hint) : undefined,
      }))
    },

    async журнал(сущность, id): Promise<ЗаписьЖурнала[]> {
      const тело = await взять<{ rows: СтрокаЖурнала[] }>('log', { entity: сущность, id })
      return (тело.rows ?? []).map((с) => вЗаписьЖурнала(сущность, с))
    },

    async документы(сущность, id): Promise<Файл[]> {
      const тело = await взять<{ rows: СтрокаФайла[] }>('files', { entity: сущность, id })
      return (тело.rows ?? []).map(вФайл)
    },

    async лента(сущность, id): Promise<Событие[]> {
      // Ленты в своей схеме нет вовсе — ядро отвечает 501 и объясняет, чего нет. Пустой
      // массив здесь означал бы «по карточке ничего не происходило», а это не так: события
      // существуют, они лежат в Bitrix, и переносить их владелец не решал.
      const тело = await взять<{ rows: Событие[] }>('feed', { entity: сущность, id })
      return тело.rows ?? []
    },

    async обратные(сущность, id, код): Promise<СтрокаСвязи[]> {
      const связь = описание(сущность).обратные.find((о) => о.код === код)
      if (!связь) return []
      const тело = await взять<{ rows: { id: string; values: Record<string, unknown> }[] }>(
        'back',
        {
          entity: связь.сущность,
          field: связь.поле,
          id,
          // Колонки называет ОПИСАНИЕ: ядро описаний карточек не знает и знать не должно,
          // иначе перечень колонок жил бы в двух местах и разошёлся бы при первой правке.
          cols: связь.колонки.map((к) => к.код).join(','),
        },
      )
      return (тело.rows ?? []).map((с) => ({
        id: String(с.id),
        значения: значенияИзЯдра(связь.сущность, с.values ?? {}),
      }))
    },

    async сохранить(сущность): Promise<ИтогСохранения> {
      // Ни запроса, ни «оптимистичного» обновления на экране: карточка обязана остаться
      // в том виде, в каком её видит база. Ручки записи в ядре нет вовсе — этот отказ
      // не «заглушка на будущее», а сегодняшняя правда о разделе.
      throw new ЗаписьНеВключена(сущность)
    },
  }
}

/** Один источник на приложение: у него нет своего состояния, копии тут ни к чему. */
export const источникКарточекBFF: ИсточникКарточек = источникBFF()
